/**
 * paso-actividades-seeder.ts — pure helper for robust IS actividades seeding (B-T3).
 *
 * Determines whether actividades should be seeded from panelFotografico descriptions.
 * The rule is simple: seed ONLY when actividades is currently empty.
 * This prevents clobbering user-edited actividades (S-8b.1 guard, S-8b.3).
 *
 * Used by InformeEditor's useEffect when the IS actividades step becomes active.
 *
 * // ponytail: pure function, no side effects. The useEffect in InformeEditor
 * // calls this and sets state. Ceiling = if the seeding logic grows (e.g.
 * // matching by foto index, partial updates), extract a richer strategy here.
 */

import type { IeFoto, IsActividad } from "../../types.js";

/**
 * Returns the actividades to use for the current step (automatic first-visit seed).
 *
 * - If `current` has ANY item with real (non-blank) text: returns it unchanged so the
 *   técnico's edits are never clobbered.
 * - Otherwise (empty OR all-blank items): seeds from `panel`, SKIPPING blank slots so
 *   empty photo slots don't produce empty actividades. This also CLEANS UP reports that
 *   were previously seeded with blank placeholder items. If every slot is blank the
 *   result is empty, so a later visit (after the panel is filled) re-seeds automatically.
 *
 * @param current - The current actividades array from IsPayload.
 * @param panel   - The panelFotografico array from IsPayload.
 * @returns       - The actividades to set in state.
 */
export function seedActividadesFromPanel(
  current: IsActividad[],
  panel: IeFoto[],
): IsActividad[] {
  // Preserve real user content. "Real" = at least one non-blank description.
  // Blank-only arrays (incl. legacy reports seeded with empty placeholders) are
  // treated as unseeded so we can replace them with the filtered panel descriptions.
  const tieneContenidoReal = current.some((a) => a.descripcion.trim().length > 0);
  if (tieneContenidoReal) return current;

  // Seed only slots that actually have a description — no empty items.
  return panel
    .filter((f) => f.descripcion.trim().length > 0)
    .map((f) => ({ descripcion: f.descripcion }));
}

/**
 * Manual "pull descriptions from panel" merge. Unlike the automatic seeder, this
 * runs on demand (a button) even when actividades already has items: it APPENDS the
 * panel descriptions not already present, as ordinary editable/removable actividades.
 * It never replaces or reorders what the técnico already wrote.
 *
 * Dedup is by trimmed description text, so pressing the button twice adds nothing new.
 *
 * @param current - The current actividades array.
 * @param panel   - The panelFotografico array.
 * @returns       - current followed by any new non-empty panel descriptions.
 */
export function mergeActividadesFromPanel(
  current: IsActividad[],
  panel: IeFoto[],
): IsActividad[] {
  const yaPresentes = new Set(current.map((a) => a.descripcion.trim()));
  const nuevas = panel
    .map((f) => f.descripcion.trim())
    .filter((d) => d.length > 0 && !yaPresentes.has(d))
    .map((descripcion) => ({ descripcion }));
  return nuevas.length > 0 ? [...current, ...nuevas] : current;
}
